import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'
import { Icon, type IconName } from './icon.tsx'

const tagVariants = cva(
	'font-parkinsans focus-visible:ring-ring inline-flex items-center justify-center font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50',
	{
		variants: {
			variant: {
				primary: '',
				secondary: '',
			},
			appearance: {
				solid: '',
				outline: '',
			},
			size: {
				s: 'text-tag-s h-6 gap-1 px-2',
				l: 'text-tag-l h-[34px] gap-1.5 px-3',
			},
			corner: {
				round: '', // Will be set in compoundVariants
				rounded: '', // Will be set in compoundVariants
			},
			color: {
				green: '',
				blue: '',
			},
		},
		compoundVariants: [
			// PRIMARY GREEN SOLID
			{
				variant: 'primary',
				appearance: 'solid',
				color: 'green',
				className:
					'bg-green-500 text-neutral-50 hover:bg-green-600 active:bg-green-700',
			},
			// PRIMARY GREEN OUTLINE
			{
				variant: 'primary',
				appearance: 'outline',
				color: 'green',
				className:
					'border border-green-500 bg-transparent text-green-500 hover:bg-green-50 active:bg-green-100',
			},
			// SECONDARY GREEN SOLID
			{
				variant: 'secondary',
				appearance: 'solid',
				color: 'green',
				className:
					'bg-green-50 text-green-500 hover:bg-green-100 active:bg-green-200',
			},
			// SECONDARY GREEN OUTLINE
			{
				variant: 'secondary',
				appearance: 'outline',
				color: 'green',
				className:
					'border border-green-300 bg-transparent text-green-500 hover:bg-green-50 active:bg-green-100',
			},
			// PRIMARY BLUE SOLID
			{
				variant: 'primary',
				appearance: 'solid',
				color: 'blue',
				className:
					'bg-blue-500 text-neutral-50 hover:bg-blue-600 active:bg-blue-700',
			},
			// PRIMARY BLUE OUTLINE
			{
				variant: 'primary',
				appearance: 'outline',
				color: 'blue',
				className:
					'border border-blue-500 bg-transparent text-blue-500 hover:bg-blue-50 active:bg-blue-100',
			},
			// SECONDARY BLUE SOLID
			{
				variant: 'secondary',
				appearance: 'solid',
				color: 'blue',
				className:
					'bg-blue-50 text-blue-500 hover:bg-blue-100 active:bg-blue-200',
			},
			// SECONDARY BLUE OUTLINE
			{
				variant: 'secondary',
				appearance: 'outline',
				color: 'blue',
				className:
					'border border-blue-300 bg-transparent text-blue-500 hover:bg-blue-50 active:bg-blue-100',
			},
			// CORNER VARIANTS - Small Size
			{
				size: 's',
				corner: 'round',
				className: 'rounded-[4px]',
			},
			{
				size: 's',
				corner: 'rounded',
				className: 'rounded-[4px]',
			},
			// CORNER VARIANTS - Large Size
			{
				size: 'l',
				corner: 'round',
				className: 'rounded-[4px]',
			},
			{
				size: 'l',
				corner: 'rounded',
				className: 'rounded-[4px]',
			},
		],
		defaultVariants: {
			variant: 'primary',
			appearance: 'solid',
			size: 's',
			corner: 'round',
			color: 'green',
		},
	},
)

export interface TagProps
	extends React.ComponentProps<'span'>,
		VariantProps<typeof tagVariants> {
	/**
	 * Tag semantic variant - primary for main actions, secondary for supporting
	 */
	variant?: 'primary' | 'secondary'
	/**
	 * Tag appearance style - solid for filled background, outline for border only
	 */
	appearance?: 'solid' | 'outline'
	/**
	 * Tag size - s for small (24px height), l for large (34px height)
	 */
	size?: 's' | 'l'
	/**
	 * Tag corner style - round for fully rounded (pill), rounded for standard border radius
	 */
	corner?: 'round' | 'rounded'
	/**
	 * Tag color scheme - green or blue following design system
	 */
	color?: 'green' | 'blue'
	/**
	 * Content type - determines what's displayed in the tag
	 */
	option?:
		| 'number'
		| 'text'
		| 'time'
		| 'icon'
		| 'text-icon-left'
		| 'text-icon-right'
		| 'text-2-icons'
		| 'custom'
	/**
	 * Icon to display (for icon-based options)
	 */
	icon?: IconName
	/**
	 * Left icon name (for text-icon-left and text-2-icons options)
	 */
	leftIcon?: IconName
	/**
	 * Right icon name (for text-icon-right and text-2-icons options)
	 */
	rightIcon?: IconName
	/**
	 * Custom left icon size (overrides default size based on tag size)
	 */
	leftIconSize?: number
	/**
	 * Custom right icon size (overrides default size based on tag size)
	 */
	rightIconSize?: number
	/**
	 * Custom left icon className
	 */
	leftIconClassName?: string
	/**
	 * Custom right icon className
	 */
	rightIconClassName?: string
	/**
	 * Number value (for number option)
	 */
	number?: number | string
	/**
	 * Time value (for time option)
	 */
	time?: string
	/**
	 * Tag text content
	 */
	children?: React.ReactNode
}

const Tag = React.forwardRef<HTMLSpanElement, TagProps>(
	(
		{
			className,
			variant = 'primary',
			appearance = 'solid',
			size = 's',
			corner = 'round',
			color = 'green',
			option = 'text',
			icon,
			leftIcon,
			rightIcon,
			leftIconSize,
			rightIconSize,
			leftIconClassName,
			rightIconClassName,
			number,
			time,
			children,
			...props
		},
		ref,
	) => {
		// Icon size based on tag size
		const iconSize = size === 's' ? 'sm' : 'md'

		// Render content based on option type
		const renderContent = () => {
			switch (option) {
				case 'number':
					return number || '1'

				case 'time':
					return time || '10:30'

				case 'icon':
					return icon ? (
						<Icon name={icon} size={iconSize} />
					) : (
						<Icon name="avatar" size={iconSize} />
					)

				case 'text-icon-left':
					return (
						<>
							{leftIcon && (
								<Icon
									name={leftIcon}
									size={iconSize}
									className={leftIconClassName}
									style={
										leftIconSize
											? { width: leftIconSize, height: leftIconSize }
											: undefined
									}
								/>
							)}
							{children || 'Text'}
						</>
					)

				case 'text-icon-right':
					return (
						<>
							{children || 'Text'}
							{rightIcon && (
								<Icon
									name={rightIcon}
									size={iconSize}
									className={rightIconClassName}
									style={
										rightIconSize
											? { width: rightIconSize, height: rightIconSize }
											: undefined
									}
								/>
							)}
						</>
					)

				case 'text-2-icons':
					return (
						<>
							{leftIcon && (
								<Icon
									name={leftIcon}
									size={iconSize}
									className={leftIconClassName}
									style={
										leftIconSize
											? { width: leftIconSize, height: leftIconSize }
											: undefined
									}
								/>
							)}
							{children || 'Text'}
							{rightIcon && (
								<Icon
									name={rightIcon}
									size={iconSize}
									className={rightIconClassName}
									style={
										rightIconSize
											? { width: rightIconSize, height: rightIconSize }
											: undefined
									}
								/>
							)}
						</>
					)

				case 'custom':
					// For fully custom content, just render children
					return children

				default: // 'text'
					return children || 'Tag'
			}
		}

		return (
			<span
				ref={ref}
				className={cn(
					tagVariants({
						variant,
						appearance,
						size,
						corner,
						color,
					}),
					className,
				)}
				{...props}
			>
				{renderContent()}
			</span>
		)
	},
)

Tag.displayName = 'Tag'

export { Tag, tagVariants }
